/**
 * Shared-компонент «Форма рабочего места» (centrika-docs!78, `component.workplace.form`) — единый
 * для вариантов «Добавление» и «Изменение», отображается в диалоговом окне. НЕ ПУТАТЬ с
 * `sout/workplace-form.tsx` (форма условий труда в заказе СОУТ — другое).
 *
 * Общие поля рабочего места (одинаковы в обоих вариантах, критерий приёмки 1): «Подразделение»
 * (обяз., `DepartmentSelect`, паттерн !70/!61 — deferCreate: новое подразделение заводится вместе
 * с местом), «Должность» (обяз., `PositionSelect`, allowCreate), «Фактический адрес рабочего
 * места» (необяз. текст), «Продолжительность смены, часов» и «Количество рабочих часов в неделю»
 * (необяз., целое положительное при заполнении), «График работы» (необяз. текст).
 *
 * Вариант «Добавление» (`variant='create'`): заголовок «Добавление рабочего места»; дополнительно
 * поля «Сотрудник» (+ «Добавить сотрудника»/«Без сотрудника»/существующий) и «Сменщик»; сохранение
 * — `POST /company/workplaces` (`api.createWorkplace`) с композицией сотрудник/сменщик.
 *
 * Вариант «Изменение» (`variant='edit'`): заголовок «Редактирование рабочего места»; открывается с
 * текущими значениями (`initial`); разделов «Сотрудник»/«Сменщик» НЕТ (связанные сотрудники и
 * сменщики не меняются — `hasNoEmployee` сохраняется как есть); сохранение — `PATCH
 * /company/workplaces/{id}` (`api.updateWorkplace`). Фактический адрес/график можно очистить
 * (шлём пустую строку — бэкенд перезапишет); часы, если заполнены, должны быть целым положительным.
 *
 * Кнопки «Сохранить»/«Отмена»; во время сохранения недоступны, основная показывает «Сохранение…»;
 * ошибка не закрывает форму и не очищает данные (`error` только читается). Текст ошибки по варианту:
 * «Не удалось создать рабочее место» / «Не удалось изменить рабочее место». Дубль подразделения —
 * «Такое подразделение уже есть» (показывает сам `DepartmentSelect`).
 *
 * `EmployeeSelect`/`PositionSelect` — не Radix (портал в `document.body`), поэтому реальные
 * взаимодействия внутри Radix `Dialog` в jsdom безопасны (см. докстринг прежнего
 * `add-workplace-form.tsx`).
 */
import { useState, type FormEvent } from 'react'
import {
  type CompanyScopeApi,
  type CreateWorkplaceInput,
  type Department,
  type Employee,
  type Workplace,
  type WorkplacePersonInput,
} from '@/lib/api'
import { getErrorMessage, fieldError } from '@/lib/errors'
import { validateFio } from '@/lib/validation'
import { isNotFutureDate, isDateOrderValid } from '@/lib/date-order'
import { cn } from '@/lib/utils'
import { formatWorkHours, isValidWorkHours, parseWorkHours, WORK_HOURS_ERROR } from '@/lib/work-hours'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { EducationSelect } from '@/company/education-select'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { SnilsInput } from '@/design-system/components/primitives/snils-input'
import { SNILS_MESSAGE } from '@/lib/snils'
import { isValidSnils } from '@/lib/validation'
import { Button } from '@/design-system/components/primitives/button'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { Alert } from '@/design-system/components/feedback/alert'
import { Grid } from '@/design-system/components/layout/grid'
import { DepartmentSelect } from './department-select'
import { PositionSelect } from './position-select'
import { EMPLOYEE_SELECT_ADD, EmployeeSelect, type EmployeeSelectOption } from './employee-select'
import { normalizePersonName } from '@/lib/name-case'

const NO_EMPLOYEE = ''
const NEW_EMPLOYEE = '__new_employee__'
const NO_PARTNER = ''

export type WorkplaceFormVariant = 'create' | 'edit'

/** Предзаполнение полей рабочего места для варианта «Изменение» (текущие значения места). */
export interface WorkplaceFormInitial {
  department: string
  departmentId?: string
  position: string
  positionId?: string
  actualAddress: string
  shiftHours: number | null
  weekHours: number | null
  schedule: string
  /** Признак «без сотрудника» текущего места — сохраняется без изменений при PATCH. */
  hasNoEmployee: boolean
}

function employeeLabel(e: Employee): string {
  return `${e.fullName} — ${e.departmentName} · ${e.position}`
}

/** Опции выбора сотрудника: метка с подразделением/должностью, поиск — только по ФИО (MR !22). */
function employeeOptions(list: Employee[]): EmployeeSelectOption[] {
  return list.map((e) => ({ value: e.id, label: employeeLabel(e), fullName: e.fullName }))
}

/** Допустимый ввод часов — в показ с запятой («37.5» → «37,5»); прочее остаётся как введено. */
function displayHours(v: string): string {
  const n = parseWorkHours(v)
  return isValidWorkHours(n) ? formatWorkHours(n) : v
}

/**
 * Часы (смена/неделя): пусто — допустимо; заполнено — число больше 0, не более двух знаков после
 * запятой (fractional-work-hours; запятая или точка — `lib/work-hours`).
 */
function hoursError(v: string): string | undefined {
  const n = parseWorkHours(v)
  if (n === null || isValidWorkHours(n)) return undefined
  return WORK_HOURS_ERROR
}

interface NewEmployeeState {
  fullName: string
  gender: 'm' | 'f'
  birthDate: string
  snils: string
  email: string
  phone: string
  education: string
  hasDisability: boolean
  hiredOn: string
  rank: string
}

const EMPTY_NEW_EMPLOYEE: NewEmployeeState = {
  fullName: '',
  gender: 'm',
  birthDate: '',
  snils: '',
  email: '',
  phone: '',
  education: '',
  hasDisability: false,
  hiredOn: '',
  rank: '',
}

export interface WorkplaceFormProps {
  api: CompanyScopeApi
  /** Вариант формы: «Добавление» (create) или «Изменение» (edit). */
  variant: WorkplaceFormVariant
  /** Существующие подразделения структуры — выбор/ввод в поле «Подразделение» (centrika-docs!61). */
  departments: Department[]
  /** Существующие сотрудники — выбор основного сотрудника/сменщика (только вариант «Добавление»). */
  employees?: Employee[]
  /** Предзаполнение и id места — обязательны для варианта «Изменение». */
  initial?: WorkplaceFormInitial
  workplaceId?: string
  /** Место создано/изменено (композитный ответ в create; обновлённое место в edit). */
  onSaved: (workplace: Workplace) => void
  /** Отмена — без побочных эффектов. Если не задана — кнопка «Отмена» не рендерится. */
  onCancel?: () => void
  className?: string
}

export function WorkplaceForm({
  api,
  variant,
  departments,
  employees = [],
  initial,
  workplaceId,
  onSaved,
  onCancel,
  className,
}: WorkplaceFormProps) {
  const isEdit = variant === 'edit'

  // Подразделение: `department` — наименование; `departmentId` задан ТОЛЬКО когда выбрано
  // существующее из списка. Новое имя уходит как departmentName и создаётся вместе с местом.
  const [department, setDepartment] = useState(initial?.department ?? '')
  const [departmentId, setDepartmentId] = useState<string | undefined>(initial?.departmentId)
  const [position, setPosition] = useState(initial?.position ?? '')
  const [positionId, setPositionId] = useState<string | undefined>(initial?.positionId)

  // Поля режима работы (одинаковы в обоих вариантах).
  const [actualAddress, setActualAddress] = useState(initial?.actualAddress ?? '')
  const [shiftHours, setShiftHours] = useState(formatWorkHours(initial?.shiftHours))
  const [weekHours, setWeekHours] = useState(formatWorkHours(initial?.weekHours))
  const [schedule, setSchedule] = useState(initial?.schedule ?? '')

  // '' — без сотрудника; NEW_EMPLOYEE — «+ Добавить сотрудника»; иначе — id существующего.
  const [employeeChoice, setEmployeeChoice] = useState(NO_EMPLOYEE)
  const [newEmployee, setNewEmployee] = useState<NewEmployeeState>(EMPTY_NEW_EMPLOYEE)
  // '' — без сменщика; иначе — id существующего (backend принимает ровно одного сменщика).
  const [partnerChoice, setPartnerChoice] = useState(NO_PARTNER)

  const [error, setError] = useState<unknown>(null)
  const [submitting, setSubmitting] = useState(false)

  function setNewField<K extends keyof NewEmployeeState>(key: K, value: NewEmployeeState[K]) {
    setNewEmployee((f) => ({ ...f, [key]: value }))
  }

  // Разделы «Сотрудник»/«Сменщик» — только в варианте «Добавление».
  const hasEmployee = !isEdit && employeeChoice !== NO_EMPLOYEE
  const isNewEmployee = !isEdit && employeeChoice === NEW_EMPLOYEE

  // Дубль НОВОГО имени подразделения (свободный ввод дублирует существующее): ошибка, submit
  // блокируется — нужно выбрать существующее из списка либо ввести уникальное новое наименование.
  const departmentDuplicate =
    !departmentId &&
    department.trim() !== '' &&
    departments.some((d) => d.name.trim().toLowerCase() === department.trim().toLowerCase())
  const placeValid = department.trim() !== '' && position.trim() !== '' && !departmentDuplicate

  const shiftHoursError = hoursError(shiftHours)
  const weekHoursError = hoursError(weekHours)
  const conditionsValid = !shiftHoursError && !weekHoursError

  const fioInvalid =
    isNewEmployee && newEmployee.fullName.trim() !== '' && !validateFio(newEmployee.fullName)
  const birthFuture = isNewEmployee && !isNotFutureDate(newEmployee.birthDate)
  const hiredFuture = isNewEmployee && !isNotFutureDate(newEmployee.hiredOn)
  const hiredBeforeBirth =
    isNewEmployee && !isDateOrderValid(newEmployee.birthDate, newEmployee.hiredOn)
  const newEmployeeValid =
    !isNewEmployee ||
    (newEmployee.fullName.trim() !== '' &&
      validateFio(newEmployee.fullName) &&
      !birthFuture &&
      !hiredFuture &&
      !hiredBeforeBirth)
  const snilsError =
    newEmployee.snils.trim() !== '' && !isValidSnils(newEmployee.snils) ? SNILS_MESSAGE : undefined
  const valid = placeValid && conditionsValid && newEmployeeValid && !snilsError

  // Кандидаты в сменщики — существующие сотрудники, кроме выбранного основным.
  const partnerCandidates = employees.filter((e) => e.id !== employeeChoice)

  const defaultErrorMessage = isEdit
    ? 'Не удалось изменить рабочее место'
    : 'Не удалось создать рабочее место'

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!valid || submitting) return
    setSubmitting(true)
    try {
      const input: Partial<CreateWorkplaceInput> = {
        departmentId,
        departmentName: departmentId ? undefined : department.trim(),
        position: position.trim(),
        positionId,
        // Вариант «Изменение» сохраняет текущий признак «без сотрудника» (связанные сотрудники не
        // меняются); «Добавление» выводит его из выбора сотрудника.
        hasNoEmployee: isEdit ? (initial?.hasNoEmployee ?? false) : !hasEmployee,
      }
      // Адрес/график: в «Изменении» шлём строку всегда (пустая строка очищает поле — backend
      // перезаписывает); в «Добавлении» пустое не шлём (payload остаётся как раньше).
      if (isEdit) {
        input.actualAddress = actualAddress.trim()
        input.schedule = schedule.trim()
      } else {
        input.actualAddress = actualAddress.trim() || undefined
        input.schedule = schedule.trim() || undefined
      }
      // Часы: шлём только допустимое число (иначе поле остаётся текущим — backend
      // COALESCE-патч; пустое очищение часов в «Изменении» не поддержано, см. отчёт). Ввод с
      // запятой разбирается в число (`parseWorkHours`): «7,5» → 7.5.
      const shiftHoursValue = parseWorkHours(shiftHours)
      const weekHoursValue = parseWorkHours(weekHours)
      if (isValidWorkHours(shiftHoursValue)) input.shiftHours = shiftHoursValue
      if (isValidWorkHours(weekHoursValue)) input.weekHours = weekHoursValue

      if (!isEdit) {
        if (isNewEmployee) {
          const emp: WorkplacePersonInput = {
            fullName: newEmployee.fullName.trim(),
            gender: newEmployee.gender,
            birthDate: newEmployee.birthDate || undefined,
            snils: newEmployee.snils.trim() || undefined,
            email: newEmployee.email.trim() || undefined,
            phone: newEmployee.phone.trim() || undefined,
            education: newEmployee.education.trim() || undefined,
            hasDisability: newEmployee.hasDisability,
            hiredOn: newEmployee.hiredOn || undefined,
            rank: newEmployee.rank.trim() || undefined,
          }
          input.employee = emp
        } else if (hasEmployee) {
          input.employee = { personId: employeeChoice }
        }
        if (hasEmployee && partnerChoice !== NO_PARTNER) {
          input.shiftPartner = { personId: partnerChoice }
        }
      }

      const saved =
        isEdit && workplaceId
          ? await api.updateWorkplace(workplaceId, input)
          : await api.createWorkplace(input as CreateWorkplaceInput)
      onSaved(saved)
    } catch (err) {
      setError(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate className={cn(className)}>
      {error ? (
        <Alert variant="danger" className="mb-4">
          {getErrorMessage(error, defaultErrorMessage)}
        </Alert>
      ) : null}

      <Grid cols={2}>
        <Field
          label="Подразделение"
          className="my-0"
          required
          // Дубль показывает сам DepartmentSelect у поля ввода (deferredDupEntry) — здесь только
          // серверные ошибки, чтобы сообщение не задваивалось.
          error={fieldError(error, 'departmentName') ?? fieldError(error, 'departmentId')}
        >
          {(props) => (
            <DepartmentSelect
              departments={departments}
              value={department}
              onSelect={(name, id) => {
                setDepartment(name)
                setDepartmentId(id)
              }}
              deferCreate
              id={props.id}
              aria-required={props['aria-required']}
              placeholder="Выберите или введите отдел"
            />
          )}
        </Field>
        <Field label="Должность" className="my-0" required error={fieldError(error, 'position')}>
          {(props) => (
            <PositionSelect
              value={positionId}
              valueName={position}
              onChange={(id, name) => {
                setPositionId(id)
                setPosition(name)
              }}
              allowCreate
              id={props.id}
              aria-required={props['aria-required']}
              required
            />
          )}
        </Field>
      </Grid>

      <Field label="Фактический адрес рабочего места" className="mb-0 mt-3">
        {(props) => (
          <Input
            {...props}
            value={actualAddress}
            onChange={(e) => setActualAddress(e.target.value)}
          />
        )}
      </Field>

      <Grid cols={2} className="mt-3">
        <Field
          label="Продолжительность смены, часов"
          className="my-0"
          error={fieldError(error, 'shiftHours') ?? shiftHoursError}
        >
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={shiftHours}
              invalid={props['aria-invalid'] || Boolean(shiftHoursError)}
              onChange={(e) => setShiftHours(e.target.value)}
              onBlur={() => setShiftHours(displayHours)}
            />
          )}
        </Field>
        <Field
          label="Количество рабочих часов в неделю"
          className="my-0"
          error={fieldError(error, 'weekHours') ?? weekHoursError}
        >
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={weekHours}
              invalid={props['aria-invalid'] || Boolean(weekHoursError)}
              onChange={(e) => setWeekHours(e.target.value)}
              onBlur={() => setWeekHours(displayHours)}
            />
          )}
        </Field>
      </Grid>

      <Field label="График работы" className="mb-0 mt-3">
        {(props) => (
          <Input {...props} value={schedule} onChange={(e) => setSchedule(e.target.value)} />
        )}
      </Field>

      {!isEdit ? (
        <Field
          label="Сотрудник"
          className="mb-0 mt-3"
          hint={
            hasEmployee && !isNewEmployee
              ? 'Выбранный сотрудник переедет на это рабочее место.'
              : 'Рабочее место можно сохранить без сотрудника.'
          }
          error={fieldError(error, 'employee') ?? fieldError(error, 'employee.personId')}
        >
          {(props) => (
            // Порядок общего требования (centrika-docs, common.md, MR !22): «+ Добавить сотрудника»
            // → разделительная черта → «Без сотрудника» → сотрудники.
            <EmployeeSelect
              id={props.id}
              value={employeeChoice}
              valueLabel={employeeChoice === NEW_EMPLOYEE ? EMPLOYEE_SELECT_ADD : undefined}
              aria-invalid={props['aria-invalid']}
              aria-describedby={props['aria-describedby']}
              invalid={props['aria-invalid']}
              noneOption={{ value: NO_EMPLOYEE, label: 'Без сотрудника' }}
              options={employeeOptions(employees)}
              onAddNew={() => {
                setEmployeeChoice(NEW_EMPLOYEE)
                setPartnerChoice(NO_PARTNER)
              }}
              onSelect={(v) => {
                setEmployeeChoice(v)
                setPartnerChoice(NO_PARTNER)
              }}
            />
          )}
        </Field>
      ) : null}

      {isNewEmployee ? (
        <div className="rounded-9 border border-line p-3.5">
          <h3 className="mb-3 text-12 font-semibold uppercase tracking-wide text-txt-secondary">
            Новый сотрудник
          </h3>
          <Field
            label="ФИО"
            className="my-0"
            required
            hint="Полностью, минимум 3 слова — без инициалов"
            error={
              fieldError(error, 'employee.fullName') ??
              (fioInvalid ? 'Проверьте ФИО (3 слова, без инициалов)' : undefined)
            }
          >
            {(props) => (
              <Input
                {...props}
                value={newEmployee.fullName}
                onChange={(e) => setNewField('fullName', e.target.value)}
                // «Иванов Иван Иванович» — на уходе с поля (names-and-positions-normalization).
                onBlur={() => setNewField('fullName', normalizePersonName(newEmployee.fullName))}
                spellCheck={false}
                required
              />
            )}
          </Field>

          <Grid cols={2} className="mt-3">
            <Field label="Пол" className="my-0">
              {() => (
                <div className="flex gap-2" role="group" aria-label="Пол">
                  <Button
                    type="button"
                    size="sm"
                    variant={newEmployee.gender === 'm' ? 'primary' : 'secondary'}
                    aria-pressed={newEmployee.gender === 'm'}
                    onClick={() => setNewField('gender', 'm')}
                  >
                    Мужской
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={newEmployee.gender === 'f' ? 'primary' : 'secondary'}
                    aria-pressed={newEmployee.gender === 'f'}
                    onClick={() => setNewField('gender', 'f')}
                  >
                    Женский
                  </Button>
                </div>
              )}
            </Field>
            <Field
              label="Дата рождения"
              className="my-0"
              error={
                fieldError(error, 'employee.birthDate') ??
                (birthFuture ? 'Дата рождения не может быть в будущем' : undefined)
              }
            >
              {(props) => (
                <DateInput
                  {...props}
                  value={newEmployee.birthDate}
                  invalid={props['aria-invalid'] || birthFuture}
                  onChange={(birthDate) => setNewField('birthDate', birthDate)}
                />
              )}
            </Field>
          </Grid>

          <Grid cols={2} className="mt-3">
            <Field
              label="СНИЛС"
              className="my-0"
              hint="нужен для анкеты СОУТ"
              error={fieldError(error, 'employee.snils') ?? snilsError}
            >
              {(props) => (
                <SnilsInput
                  {...props}
                  value={newEmployee.snils}
                  invalid={Boolean(snilsError)}
                  onChange={(snils) => setNewField('snils', snils)}
                />
              )}
            </Field>
            <Field
              label="Дата приёма на работу"
              className="my-0"
              error={
                fieldError(error, 'employee.hiredOn') ??
                (hiredFuture
                  ? 'Дата приёма не может быть в будущем'
                  : hiredBeforeBirth
                    ? 'Дата приёма не может быть раньше даты рождения'
                    : undefined)
              }
            >
              {(props) => (
                <DateInput
                  {...props}
                  value={newEmployee.hiredOn}
                  invalid={props['aria-invalid'] || hiredFuture || hiredBeforeBirth}
                  onChange={(hiredOn) => setNewField('hiredOn', hiredOn)}
                />
              )}
            </Field>
          </Grid>

          <Grid cols={2} className="mt-3">
            <Field label="Email" className="my-0" error={fieldError(error, 'employee.email')}>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewField('email', e.target.value)}
                />
              )}
            </Field>
            <Field label="Телефон" className="my-0" error={fieldError(error, 'employee.phone')}>
              {(props) => (
                <Input
                  {...props}
                  type="tel"
                  value={newEmployee.phone}
                  onChange={(e) => setNewField('phone', e.target.value)}
                />
              )}
            </Field>
          </Grid>

          <Grid cols={2} className="mt-3">
            <Field label="Образование" className="my-0">
              {(props) => (
                <EducationSelect
                  id={props.id}
                  value={newEmployee.education}
                  onChange={(v) => setNewField('education', v)}
                />
              )}
            </Field>
            <Field label="Разряд" className="my-0">
              {(props) => (
                <Input
                  {...props}
                  value={newEmployee.rank}
                  onChange={(e) => setNewField('rank', e.target.value)}
                />
              )}
            </Field>
          </Grid>

          <div className="mt-1 flex items-center gap-2.5 py-1">
            <Checkbox
              id="workplace-form-new-employee-disability"
              checked={newEmployee.hasDisability}
              onCheckedChange={(v) => setNewField('hasDisability', v === true)}
            />
            <label htmlFor="workplace-form-new-employee-disability" className="text-13 text-txt">
              Есть инвалидность
            </label>
          </div>
        </div>
      ) : null}

      {hasEmployee ? (
        <Field
          label="Сменщик"
          className="mb-0 mt-3"
          hint="Необязательно — сотрудник и сменщик свяжутся с одним рабочим местом."
          error={fieldError(error, 'shiftPartner.personId')}
        >
          {(props) => (
            <EmployeeSelect
              id={props.id}
              value={partnerChoice}
              aria-describedby={props['aria-describedby']}
              noneOption={{ value: NO_PARTNER, label: 'Без сменщика' }}
              options={employeeOptions(partnerCandidates)}
              onSelect={setPartnerChoice}
            />
          )}
        </Field>
      ) : null}

      <div className="mt-4 flex items-center gap-2">
        <Button type="submit" disabled={submitting || !valid}>
          {submitting ? 'Сохранение…' : 'Сохранить'}
        </Button>
        {onCancel ? (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Отмена
          </Button>
        ) : null}
      </div>
    </form>
  )
}
